import React, {
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  useCallback,
} from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Text, type Sizes, type Themes } from "@/components/ui/text/text";
import PredictionCard from "@/features/predictions/components/prediction/prediction-card";
import { useFeatures } from "@/hooks";
import { cn } from "@/utils/cn";
import { onlyEmoji as isOnlyEmoji } from "@/utils/rich-content";
import { getStatusMentions } from "@/utils/status";

import { isRtl } from "../rtl";

import Markup from "./markup";
import OutlineBox from "./outline-box";
import TranslationButton from "./statuses/translation-button";
import { Stack } from "./ui";

import type { Status, Mention, PredictionStatus } from "@/schemas";

const BIG_EMOJI_LIMIT = 10;
// Keep in sync with the `max-h-40` Tailwind class applied to the Markup below.
const MAX_CONTENT_HEIGHT = 160;

interface ReadMoreButtonProps {
  onClick: React.MouseEventHandler;
}

/** Button to expand a truncated status (due to too much content) */
const ReadMoreButton: React.FC<ReadMoreButtonProps> = ({ onClick }) => (
  <div className="relative -mt-7 w-full text-center">
    <div className="-mb-3 h-12 bg-linear-to-b from-transparent via-white to-white dark:via-gray-900 dark:to-gray-900" />
    <button
      onClick={onClick}
      type="button"
      className="h-[30px] w-full rounded-full border-2 bg-background text-sm font-medium text-gray-900 transition-all hover:bg-accent dark:text-gray-100"
    >
      <FormattedMessage
        id="status.content.show_more"
        defaultMessage="Show More"
      />
    </button>
  </div>
);

interface StatusContentProps {
  status: Status;
  onClick?: () => void;
  collapsable?: boolean;
  textSize?: Sizes;
  textTheme?: Themes;
  searchedText?: string;
  /** Hide the translate control (e.g. in non-interactive previews). */
  hideTranslation?: boolean;
}

/** Renders the text content of a status */
const StatusContent: React.FC<StatusContentProps> = ({
  status,
  onClick,
  collapsable = false,
  textSize = "md",
  textTheme = "default",
  searchedText,
  hideTranslation = false,
}) => {
  const navigate = useNavigate();
  const features = useFeatures();

  const [isClamped, setIsClamped] = useState(false);
  const [onlyEmoji, setOnlyEmoji] = useState(false);

  const node = useRef<HTMLDivElement>(null);

  const onMentionClick = (mention: Mention, e: MouseEvent) => {
    if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      navigate(`/@${mention.acct}`);
    }
  };

  const onHashtagClick = (hashtag: string, e: MouseEvent) => {
    hashtag = hashtag.replace(/^#/, "").toLowerCase();

    if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      navigate(`/tags/${hashtag}`);
    }
  };

  /** For regular links, just stop propogation */
  const onLinkClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const updateStatusLinks = () => {
    if (!node.current) return;

    const links = node.current.querySelectorAll("a");

    links.forEach((link) => {
      // Skip already processed
      if (link.classList.contains("status-link")) return;

      // Add attributes
      link.classList.add("status-link");
      link.setAttribute("rel", "nofollow noopener");
      link.setAttribute("target", "_blank");

      const mention = getStatusMentions(status).find(
        (mention) => link.href === `${mention.url}`,
      );

      // Add event listeners on mentions and hashtags
      if (mention) {
        link.addEventListener(
          "click",
          onMentionClick.bind(link, mention),
          false,
        );
        link.setAttribute("title", mention.acct);
      } else if (
        link.textContent?.charAt(0) === "#" ||
        link.previousSibling?.textContent?.charAt(
          link.previousSibling.textContent.length - 1,
        ) === "#"
      ) {
        link.addEventListener(
          "click",
          onHashtagClick.bind(link, link.text),
          false,
        );
      } else {
        link.setAttribute("title", link.href);
        link.addEventListener("click", onLinkClick.bind(link), false);
      }
    });
  };

  const maybeSetOnlyEmoji = (): void => {
    if (!node.current) return;
    const only = isOnlyEmoji(node.current, BIG_EMOJI_LIMIT, true);

    if (only !== onlyEmoji) {
      setOnlyEmoji(only);
    }
  };

  // Wrap the searched text in <strong> declaratively so React owns the markup.
  // Mutating innerHTML imperatively fights React's dangerouslySetInnerHTML —
  // re-renders re-apply the original HTML and wipe the highlight.
  const highlightSearchedText = (html: string) => {
    if (!searchedText) return html;

    // if it starts with from:username, remove it from search
    const trimmedSearchedText = searchedText.replace(/^from:(\w+)/, "").trim();

    if (trimmedSearchedText.length === 0) return html;

    const escaped = trimmedSearchedText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escaped, "gi");
    const startIndex = html.search(regex);
    if (startIndex === -1) return html;

    const originalText = html.slice(
      startIndex,
      startIndex + trimmedSearchedText.length,
    );
    return html.replace(regex, `<strong>${originalText}</strong>`);
  };

  const handleResize = useCallback(() => {
    if (node.current && collapsable) {
      setIsClamped(node.current.clientHeight >= MAX_CONTENT_HEIGHT);
    }
  }, [collapsable]);

  useLayoutEffect(() => {
    maybeSetOnlyEmoji();
    updateStatusLinks();
  }, []);

  useLayoutEffect(() => {
    handleResize();
  }, [status.contentHtml, status.translatedContent, handleResize]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const withContent = status.content.length > 0;

  const baseClassName =
    "wrap-break-word text-ellipsis overflow-hidden relative focus:outline-hidden";

  const content = {
    __html: highlightSearchedText(
      (features.translations && status.translatedContent) || status.contentHtml,
    ),
  };
  const title =
    (features.translations && status.translatedTitle) || status.title;

  const direction = isRtl(status.search_index) ? "rtl" : "ltr";
  const className = cn(baseClassName, {
    "cursor-pointer": onClick,
    "big-emoji leading-normal": onlyEmoji,
  });

  const renderTitles = () => {
    if (!title) return null;

    return (
      <Stack space={2}>
        {features.translations && !hideTranslation && (
          <TranslationButton status={status} />
        )}

        {title && (
          <Text
            size={textSize === "lg" ? "md" : "sm"}
            weight="bold"
            data-testid="status-title"
          >
            {title}
          </Text>
        )}
      </Stack>
    );
  };

  if (status.prediction) {
    return (
      <OutlineBox data-testid="status-content">
        <PredictionCard
          status={status as unknown as PredictionStatus}
          isQuoted
        />
      </OutlineBox>
    );
  }

  if (onClick) {
    return (
      <div className="relative">
        <Stack
          space={2}
          data-testid="status-content"
          key="status-content"
          className="relative"
        >
          {renderTitles()}

          {withContent && (
            <Markup
              ref={node}
              tabIndex={0}
              key="content"
              className={cn(className, {
                "max-h-40": collapsable,
              })}
              direction={direction}
              dangerouslySetInnerHTML={content}
              lang={status.language || undefined}
              size={textSize}
              theme={textTheme}
              data-testid="markup"
            />
          )}
        </Stack>

        {isClamped && <ReadMoreButton onClick={onClick} />}
      </div>
    );
  } else {
    return (
      <Stack space={2} data-testid="status-content" key="status-content">
        {renderTitles()}

        {withContent && (
          <Markup
            ref={node}
            tabIndex={0}
            key="content"
            className={cn(baseClassName, {
              "big-emoji leading-normal": onlyEmoji,
            })}
            direction={direction}
            dangerouslySetInnerHTML={content}
            lang={status.language || undefined}
            size={textSize}
            theme={textTheme}
            data-testid="markup"
          />
        )}
      </Stack>
    );
  }
};

export default React.memo(StatusContent);
